/**
 * User Profile API service.
 *
 * Thin, typed wrappers over the shared {@link apiClient}. The API base URL
 * already includes the `/api` prefix, so paths here start at `/profile`.
 */

import type {
  UserProfile,
  UserProfileUpdateInput,
  ConsentSource,
  PlanUsage,
} from '@covered-calls/shared';
import { apiClient } from './api-client';

const PROFILE_PATH = '/profile';
const PROFILE_CONSENT_PATH = '/profile/consent';

/**
 * Shape of the profile GET response.
 *
 * Additive to {@link UserProfile}: the backend composes an optional `plan`
 * usage block (`planId`, `effectiveLimit`, `positionsCreated`) onto the
 * response so the Usage_Indicator can render proactively before any rejection
 * (Decision 8). Optional so older responses without the block still type-check.
 *
 * _Requirements: 10.5_
 */
export interface ProfileWithPlan extends UserProfile {
  plan?: PlanUsage;
}

/**
 * Fetch the current user's profile.
 *
 * GET /api/profile — always resolves: the backend returns a `NOT_STARTED`
 * default profile when none has been persisted yet. The response also carries
 * an optional `plan` usage block (Decision 8).
 *
 * _Requirements: 6.1, 10.5_
 */
export async function getProfile(): Promise<ProfileWithPlan> {
  return apiClient.get<ProfileWithPlan>(PROFILE_PATH);
}

/**
 * Create or update the user's profile.
 *
 * PUT /api/profile — upserts the provided fields and applies the onboarding
 * state transition (including `dismiss`).
 *
 * _Requirements: 6.5_
 */
export async function updateProfile(input: UserProfileUpdateInput): Promise<UserProfile> {
  return apiClient.put<UserProfile>(PROFILE_PATH, input);
}

/**
 * Record a marketing-consent decision (grant or withdraw).
 *
 * POST /api/profile/consent — the `policyVersion` and timestamp are stamped
 * server-side. `source` distinguishes an onboarding decision from a later
 * change made in Settings.
 *
 * _Requirements: 6.10, 6.11_
 */
export async function recordConsent(granted: boolean, source: ConsentSource): Promise<UserProfile> {
  return apiClient.post<UserProfile>(PROFILE_CONSENT_PATH, { granted, source });
}
